// "Yeni parti" sekmesi — Geçmiş Rapor Aktarımı (Task 13).
//
// Tasarım review: 6A (satır kaldır/geri al, kısmi işleme, ✦ `touched` kuralı,
// "Mevcut projeyi aç"), 7A (localStorage taslağı + `beforeunload` + parti sınırı +
// `runWithConcurrency(4)`), 8A (durum tablosu copy'leri), 15A (< 14 px → text-2).
//
// Akış: dosyalar → `groupFilesByStem` (pdf+docx tek satır, K16 Word birincil) →
// kuyruk ≤ 4 ile presign+upload → `suggestFromFile` ile ✦ öneriler → satır hazır →
// "Partiyi işle ({ready})" yalnız HAZIR satırları gönderir.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  REPORT_IMPORT_MAX_BATCH_ROWS,
  type Client,
  type ImportEstimate,
  type ProjectsListResponse,
  type PublishedFrameworkEntry,
  type PublishedFrameworksResponse,
  type ReportImportFile,
  type SectorPackageSummary,
} from "@mahir/shared";
import { api, apiRaw } from "../../api/client";
import { listPublishedSectorPackages } from "../../api/sector-packages";
import {
  ImportBatchApiError,
  createImportBatch,
  estimateImport,
  listImports,
  suggestFromFile,
} from "../../api/report-import";
import { getClientById } from "../../api/clients";
import { useAuthStore } from "../../stores/auth-store";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  applySuggestionToRow,
  clearImportDraft,
  groupFilesByStem,
  markRowTouched,
  normalizeDraftRows,
  propagateClientSelection,
  readImportDraft,
  reduceRowsForBatch,
  runWithConcurrency,
  suggestSectorPackage,
  uploadImportFile,
  writeImportDraft,
  type ClientOption,
  type ExistingImportKey,
  type ImportRowField,
  type ImportRowState,
} from "../../lib/report-import-ui";
import { ImportDropzone } from "./ImportDropzone";
import { ImportRowsTable, type CreateClientResult } from "./ImportRowsTable";

const UNDO_MS = 6000;
const DRAFT_DEBOUNCE_MS = 500;
const DUP_DEBOUNCE_MS = 400;

interface DraftShape {
  rows: ImportRowState[];
}

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `r${Math.random().toString(36).slice(2)}`;
}

/**
 * "Zaten var" ön kontrolünün kimliği — sunucudaki tekillikle AYNI olmalı:
 * `report_imports` tekilliği müşteri + **framework** (sürüm DEĞİL) + yıl + dönem
 * (K22). Bu yüzden anahtar `frameworkId` taşır ve proje sorgusu da `frameworkId`
 * ile atılır (shared `ProjectsListQuerySchema.frameworkId`, fix round 1).
 */
function dupKey(row: ImportRowState): string | null {
  if (!row.clientId || !row.frameworkId || !row.reportingYear) return null;
  return `${row.clientId}|${row.frameworkId}|${row.reportingYear.toString()}|${row.periodType}`;
}

export interface NewBatchTabProps {
  /** Parti kuyruğa alınınca — sayfa İşler sekmesine geçer + 6 sn başarı bandı gösterir. */
  onBatchCreated: (count: number) => void;
}

export function NewBatchTab({ onBatchCreated }: NewBatchTabProps): JSX.Element {
  const userId = useAuthStore((s) => s.user?.id ?? "");
  const canCreateClient = useAuthStore((s) => s.user?.permissions["clients.create"] === true);
  const [rows, setRows] = useState<ImportRowState[]>([]);
  // BİLİNEN müşteriler (ClientPicker işi, 2026-10-07): 500'lük toplu liste kalktı; picker
  // sunucuda arar. Burada yalnız seçilen / oluşturulan / id'den çözülen kayıtlar durur —
  // çip adı ve paket önerisinin sektörü (`clients.sector`) buradan okunur.
  const [clients, setClients] = useState<ClientOption[]>([]);
  const rememberClient = useCallback((c: ClientOption): void => {
    setClients((prev) =>
      prev.some((x) => x.id === c.id && x.name === c.name && x.sector === c.sector)
        ? prev
        : [...prev.filter((x) => x.id !== c.id), c],
    );
  }, []);
  const [frameworks, setFrameworks] = useState<PublishedFrameworkEntry[]>([]);
  const [packagesByVersion, setPackagesByVersion] = useState<
    Record<string, SectorPackageSummary[]>
  >({});
  const [activeKeys, setActiveKeys] = useState<ExistingImportKey[]>([]);
  const [projectByKey, setProjectByKey] = useState<Record<string, string | null>>({});
  const [estimate, setEstimate] = useState<ImportEstimate | null>(null);
  const [estimateState, setEstimateState] = useState<"idle" | "pending" | "unavailable">("idle");
  const [removed, setRemoved] = useState<{ row: ImportRowState; index: number } | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<ImportRowState[] | null>(null);
  const [batchLimitNote, setBatchLimitNote] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Yüklenecek ham File nesneleri — state'e/taslağa GİRMEZ (serileştirilemez),
  // yalnız "Yeniden dene" için tutulur.
  const filesRef = useRef(new Map<string, { pdf?: File; docx?: File }>());
  const rowsRef = useRef<ImportRowState[]>([]);
  rowsRef.current = rows;

  const patchRow = useCallback(
    (id: string, patch: Partial<ImportRowState>, field?: ImportRowField): void => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const next = { ...r, ...patch };
          return field ? markRowTouched(next, field) : next;
        }),
      );
    },
    [],
  );

  /**
   * Satır içi "+ Yeni müşteri" → `POST /clients`. Başarıda kayıt yerel listeye girer,
   * satırın müşterisi seçilir (elle seçim sayılır, ✦ kalkar) ve AYNI kapak adını
   * taşıyan öteki satırlara da yayılır (`propagateClientSelection`) — bir firmanın iki
   * dosyası partide iki müşteri kaydı açmasın.
   */
  const createClientInline = useCallback(
    async (rowId: string, name: string, sector: string | null): Promise<CreateClientResult> => {
      const res = await apiRaw<{ client?: Client; message?: string }>("/clients", {
        method: "POST",
        // Sektör, paket önerisinin öncelikli kaynağı: kapak önerisinden ön-dolu gelir,
        // kullanıcı editördeki listeden değiştirebilir. Boşsa gövdeye hiç yazılmaz.
        body: { name, ...(sector ? { sector } : {}) },
      }).catch(() => null);
      const created = res?.ok ? res.body.client : undefined;
      if (!created) {
        return { ok: false, ...(res?.body?.message ? { message: res.body.message } : {}) };
      }
      setClients((prev) =>
        [
          ...prev.filter((c) => c.id !== created.id),
          { id: created.id, name: created.name, sector: created.sector },
        ].sort((a, b) => a.name.localeCompare(b.name, "tr")),
      );
      setRows((prev) => {
        // Yayılma anahtarı KAPAK adıdır (kullanıcı editörde adı düzeltmiş olabilir;
        // öteki satırlar kapaktan okunan adı taşıyor).
        const origin = prev.find((r) => r.id === rowId);
        const key = origin?.suggestedClientName ?? name;
        const withTarget = prev.map((r) =>
          r.id === rowId ? markRowTouched({ ...r, clientId: created.id }, "clientId") : r,
        );
        return propagateClientSelection(withTarget, key, created.id);
      });
      return { ok: true };
    },
    [],
  );

  // ── Referans veriler ────────────────────────────────────────────────────
  useEffect(() => {
    void api<PublishedFrameworksResponse>("/admin/frameworks/published")
      .catch(() => ({ frameworks: [] as PublishedFrameworkEntry[] }))
      .then((fw) => setFrameworks(fw.frameworks));
  }, []);

  // Satırlarda bilinmeyen `clientId` (taslaktan dönen ya da kapak önerisinin sunucuda
  // eşlediği kayıt) → tekil çağrıyla ad + sektör çözülür (çip + paket önerisi).
  const resolving = useRef(new Set<string>());
  useEffect(() => {
    const missing = [
      ...new Set(
        rows
          .map((r) => r.clientId)
          .filter(
            (id): id is string =>
              id !== null && !clients.some((c) => c.id === id) && !resolving.current.has(id),
          ),
      ),
    ];
    if (missing.length === 0) return;
    for (const id of missing) resolving.current.add(id);
    void runWithConcurrency(missing, 4, async (id) => {
      try {
        const c = await getClientById(id);
        rememberClient({ id: c.id, name: c.name, sector: c.sector });
      } catch {
        // bulunamadı/silinmiş → çip "…" kalır, kullanıcı yeniden seçer
      } finally {
        resolving.current.delete(id);
      }
    });
  }, [rows, clients, rememberClient]);

  const refreshActiveKeys = useCallback((): void => {
    void listImports({ finished: "false", pageSize: 100 })
      .then((res) =>
        setActiveKeys(
          res.imports.map((i) => ({
            clientId: i.clientId,
            frameworkId: i.frameworkId,
            reportingYear: i.reportingYear,
            periodType: i.periodType,
          })),
        ),
      )
      .catch(() => setActiveKeys([]));
  }, []);
  useEffect(refreshActiveKeys, [refreshActiveKeys]);

  // ── Taslak (7A) ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const draft = readImportDraft<DraftShape>(userId);
    if (!draft || !Array.isArray(draft.rows)) return;
    // Eski/bozuk taslaklara karşı okumada da normalleştir (yazan sürüm ne olursa olsun).
    const restorable = normalizeDraftRows(draft.rows);
    if (restorable.length > 0) setDraftPrompt(restorable);
    else clearImportDraft(userId);
  }, [userId]);

  useEffect(() => {
    if (!userId || draftPrompt) return;
    const t = window.setTimeout(() => {
      const persistable = normalizeDraftRows(rows);
      if (persistable.length === 0) clearImportDraft(userId);
      else writeImportDraft<DraftShape>(userId, { rows: persistable });
    }, DRAFT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [rows, userId, draftPrompt]);

  // G7 — unmount'ta da yaz (debounce penceresinde sekme değişirse kaybolmasın).
  useEffect(() => {
    return () => {
      if (!userId) return;
      const persistable = normalizeDraftRows(rowsRef.current);
      if (persistable.length > 0) writeImportDraft<DraftShape>(userId, { rows: persistable });
    };
  }, [userId]);

  useEffect(() => {
    if (rows.length === 0) return;
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [rows.length]);

  // ── Sektör paketleri — seçilen sürümler için tembel yükleme ──────────────
  useEffect(() => {
    const needed = [
      ...new Set(
        rows
          .map((r) => r.frameworkVersionId)
          .filter((v): v is string => v !== null && !(v in packagesByVersion)),
      ),
    ];
    if (needed.length === 0) return;
    void runWithConcurrency(needed, 4, async (versionId) => {
      const res = await listPublishedSectorPackages(versionId).catch(() => ({ packages: [] }));
      setPackagesByVersion((p) => ({ ...p, [versionId]: res.packages }));
    });
  }, [rows, packagesByVersion]);

  // Paket önerisi — müşteri kaydının sektörü, yoksa kapak önerisinin sektörü
  // (dosya adına bakan kural YOK). Yalnız dokunulmamış ve boş paket alanına yazar.
  useEffect(() => {
    // Önce hesapla, sonra YAZ: hiç öneri çıkmazsa state'e dokunulmaz (aksi halde
    // `rows` bağımlılığı sonsuz döngü olurdu — paketler/müşteri listesi değiştikçe
    // ya da kapak önerisi sektörü getirdikçe bu efekt yeniden koşar).
    const updates = new Map<string, string>();
    for (const r of rows) {
      if (!r.frameworkVersionId || r.sectorPackageId || r.touched.includes("sectorPackageId")) {
        continue;
      }
      const pkgs = packagesByVersion[r.frameworkVersionId];
      if (!pkgs || pkgs.length === 0) continue;
      const match = suggestSectorPackage(r, clients.find((c) => c.id === r.clientId) ?? null, pkgs);
      if (match) updates.set(r.id, match);
    }
    if (updates.size === 0) return;
    setRows((prev) =>
      prev.map((r) => {
        const pkgId = updates.get(r.id);
        if (!pkgId || r.sectorPackageId || r.touched.includes("sectorPackageId")) return r;
        return { ...r, sectorPackageId: pkgId, suggested: [...r.suggested, "sectorPackageId"] };
      }),
    );
  }, [rows, packagesByVersion, clients]);

  // ── "Zaten var" — mevcut proje sorgusu (debounce + kuyruk ≤ 4) ───────────
  useEffect(() => {
    const keys = [
      ...new Set(
        rows.map((r) => dupKey(r)).filter((k): k is string => k !== null && !(k in projectByKey)),
      ),
    ];
    if (keys.length === 0) return;
    const t = window.setTimeout(() => {
      void runWithConcurrency(keys, 4, async (key) => {
        const [clientId, frameworkId, year, periodType] = key.split("|");
        const qs = new URLSearchParams({
          clientId: clientId!,
          // Sürüm DEĞİL framework — aynı çerçevenin başka sürümünde açılmış proje de
          // çakışma sayılır (sunucu tekilliğiyle aynı kimlik).
          frameworkId: frameworkId!,
          reportingYear: year!,
          periodType: periodType!,
          limit: "1",
        });
        const res = await api<ProjectsListResponse>(`/projects?${qs.toString()}`).catch(() => null);
        setProjectByKey((p) => ({ ...p, [key]: res?.projects[0]?.id ?? null }));
      });
    }, DUP_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [rows, projectByKey]);

  // Görüntülenen satırlar — "zaten var" bilgisi türetilir (state'te tutulmaz).
  const displayRows = useMemo<ImportRowState[]>(
    () =>
      rows.map((r) => {
        const key = dupKey(r);
        const projectId = key ? (projectByKey[key] ?? null) : null;
        return {
          ...r,
          existingProjectId: r.existingProjectId ?? projectId,
          duplicateReason:
            r.duplicateReason ?? (projectId !== null ? ("existing_project" as const) : null),
        };
      }),
    [rows, projectByKey],
  );

  const existing = useMemo<ExistingImportKey[]>(() => {
    const fromProjects = displayRows
      .filter((r) => r.existingProjectId !== null && r.frameworkId && r.clientId && r.reportingYear)
      .map((r) => ({
        clientId: r.clientId!,
        frameworkId: r.frameworkId!,
        reportingYear: r.reportingYear!,
        periodType: r.periodType,
      }));
    return [...activeKeys, ...fromProjects];
  }, [activeKeys, displayRows]);

  const reduction = useMemo(
    () => reduceRowsForBatch(displayRows, existing),
    [displayRows, existing],
  );

  // ── Tahmin (8A) ─────────────────────────────────────────────────────────
  const pagesKey = displayRows.map((r) => r.pageCount ?? 0).join(",");
  useEffect(() => {
    const pages = displayRows
      .map((r) => r.pageCount)
      .filter((p): p is number => typeof p === "number" && p > 0);
    if (pages.length === 0) {
      setEstimate(null);
      setEstimateState(displayRows.length > 0 ? "unavailable" : "idle");
      return;
    }
    setEstimateState("pending");
    let aborted = false;
    void estimateImport(pages)
      .then((res) => {
        if (aborted) return;
        setEstimate(res);
        setEstimateState("idle");
      })
      .catch(() => {
        if (!aborted) setEstimateState("unavailable");
      });
    return () => {
      aborted = true;
    };
    // `pagesKey` satır sayfa sayılarının imzasıdır — `displayRows` nesne kimliği
    // her render'da değişeceği için dep listesine KASITLI olarak girmez.
  }, [pagesKey]);

  // ── Yükleme ─────────────────────────────────────────────────────────────
  const uploadRow = useCallback(
    async (rowId: string): Promise<void> => {
      const pair = filesRef.current.get(rowId);
      if (!pair) return;
      patchRow(rowId, { upload: { phase: "uploading", percent: 0 } });
      const ordered: File[] = [];
      if (pair.docx) ordered.push(pair.docx);
      if (pair.pdf) ordered.push(pair.pdf);
      const uploaded: ReportImportFile[] = [];
      try {
        for (const file of ordered) {
          const meta = await uploadImportFile(file, (pct) =>
            patchRow(rowId, { upload: { phase: "uploading", percent: pct } }),
          );
          // K16 — Word varsa birincil odur; `uploadImportFile` her zaman true döner.
          uploaded.push({ ...meta, primary: uploaded.length === 0 });
        }
      } catch {
        patchRow(rowId, { upload: { phase: "failed" } });
        return;
      }
      patchRow(rowId, { files: uploaded, upload: { phase: "suggesting" } });
      const primary = uploaded[0];
      if (primary) {
        // 422 `invalid_path` olamaz (presign'dan gelen path) — hata = öneri yok.
        const suggestion = await suggestFromFile(primary.storagePath).catch(() => null);
        if (suggestion) {
          setRows((prev) =>
            prev.map((r) => (r.id === rowId ? applySuggestionToRow(r, suggestion) : r)),
          );
        }
      }
      patchRow(rowId, { upload: { phase: "idle" } });
    },
    [patchRow],
  );

  const onFiles = useCallback(
    (files: File[]): void => {
      const groups = groupFilesByStem(files).filter((g) => g.pdf ?? g.docx);
      const room = Math.max(0, REPORT_IMPORT_MAX_BATCH_ROWS - rowsRef.current.length);
      const accepted = groups.slice(0, room);
      setBatchLimitNote(groups.length > room ? groups.length - room : null);
      if (accepted.length === 0) return;
      const created: ImportRowState[] = accepted.map((g) => {
        const id = newId();
        filesRef.current.set(id, {
          ...(g.pdf ? { pdf: g.pdf } : {}),
          ...(g.docx ? { docx: g.docx } : {}),
        });
        const names = [g.docx?.name, g.pdf?.name].filter((n): n is string => !!n);
        return {
          id,
          fileNames: names,
          files: [],
          pageCount: null,
          clientId: null,
          suggestedClientName: null,
          suggestedSector: null,
          frameworkVersionId: null,
          frameworkId: null,
          sectorPackageId: null,
          reportingYear: null,
          periodType: "annual",
          reportType: null,
          touched: [],
          suggested: [],
          upload: { phase: "uploading", percent: 0 },
          duplicateReason: null,
          existingProjectId: null,
        };
      });
      setRows((prev) => [...prev, ...created]);
      void runWithConcurrency(created, 4, (row) => uploadRow(row.id));
    },
    [uploadRow],
  );

  // ── Satır kaldır / geri al (6A) ─────────────────────────────────────────
  useEffect(() => {
    if (!removed) return;
    const t = window.setTimeout(() => setRemoved(null), UNDO_MS);
    return () => window.clearTimeout(t);
  }, [removed]);

  const removeRow = useCallback((id: string): void => {
    // `rowsRef` üzerinden okunur — setState updater'ı içinde başka bir setState
    // çağırmak StrictMode çift-invoke'unda iki bant açardı.
    const index = rowsRef.current.findIndex((r) => r.id === id);
    if (index === -1) return;
    setRemoved({ row: rowsRef.current[index]!, index });
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const undoRemove = useCallback((): void => {
    if (!removed) return;
    setRows((prev) => {
      const next = [...prev];
      next.splice(Math.min(removed.index, next.length), 0, removed.row);
      return next;
    });
    setRemoved(null);
  }, [removed]);

  // ── Partiyi işle ────────────────────────────────────────────────────────
  async function submit(): Promise<void> {
    if (reduction.rows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await createImportBatch({ rows: reduction.rows });
      const sentIds = new Set(reduction.rowIds);
      setRows((prev) => prev.filter((r) => !sentIds.has(r.id)));
      if (rowsRef.current.every((r) => sentIds.has(r.id))) clearImportDraft(userId);
      for (const id of sentIds) filesRef.current.delete(id);
      refreshActiveKeys();
      onBatchCreated(res.imports.length);
    } catch (err) {
      if (err instanceof ImportBatchApiError) {
        const rowId = err.rowIndex !== null ? reduction.rowIds[err.rowIndex] : undefined;
        const row = rowId ? rowsRef.current.find((r) => r.id === rowId) : undefined;
        if (rowId && err.reason) {
          // `field` GEÇİLMEZ — bu kullanıcı düzenlemesi değil, sunucu damgası
          // (`markRowTouched` damgayı sıfırlardı).
          patchRow(rowId, { duplicateReason: err.reason });
          if (err.reason === "existing_project" && row) {
            // Ön kontrol kaçırmış: önbelleği geçersiz kıl → efekt proje id'yi
            // yeniden çeker, "Mevcut projeyi aç" linki görünür.
            const key = dupKey(row);
            if (key) {
              setProjectByKey((p) => {
                const next = { ...p };
                delete next[key];
                return next;
              });
            }
          }
          if (err.reason === "active_import") refreshActiveKeys();
        }
        setSubmitError(err.message);
      } else {
        setSubmitError(err instanceof Error ? err.message : "Parti oluşturulamadı");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  // Yüklemesi süren satır zaten `eksik` (dosyası yok) → `ready`'ye girmez; düğme
  // kısmi işlemeyi engellememek için YALNIZ hazır satır yoksa pasif olur (6A).
  const ready = reduction.rows.length;

  return (
    <div className="flex flex-col gap-3">
      {draftPrompt ? (
        <div style={bandStyle("neutral")} role="status">
          <span>{fillTemplate(IMPORT_COPY.draftRestored, { n: draftPrompt.length })}</span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              type="button"
              style={smallBtn(true)}
              onClick={() => {
                setRows(draftPrompt);
                setDraftPrompt(null);
              }}
            >
              {IMPORT_COPY.draftContinue}
            </button>
            <button
              type="button"
              style={smallBtn(false)}
              onClick={() => {
                clearImportDraft(userId);
                setDraftPrompt(null);
              }}
            >
              {IMPORT_COPY.draftDiscard}
            </button>
          </span>
        </div>
      ) : null}

      <ImportDropzone
        onFiles={onFiles}
        maxFiles={Math.max(0, REPORT_IMPORT_MAX_BATCH_ROWS - rows.length)}
      />

      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-2)" }}>
          {IMPORT_COPY.dropzoneFirstUse}
        </p>
      ) : null}

      {batchLimitNote !== null ? (
        <div style={bandStyle("amber")} role="status">
          {fillTemplate(IMPORT_COPY.rejectBatchLimit, { n: batchLimitNote })}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <ImportRowsTable
          rows={displayRows}
          clients={clients}
          onClientKnown={rememberClient}
          frameworks={frameworks}
          packagesByVersion={packagesByVersion}
          existing={existing}
          canCreateClient={canCreateClient}
          onPatch={patchRow}
          onRemove={removeRow}
          onRetryUpload={(id) => void uploadRow(id)}
          onCreateClient={createClientInline}
        />
      ) : null}

      {removed ? (
        <div style={bandStyle("neutral")} role="status">
          {IMPORT_COPY.rowRemoved}
          <button type="button" onClick={undoRemove} style={{ ...smallBtn(false), marginLeft: 8 }}>
            {IMPORT_COPY.undoRemove}
          </button>
        </div>
      ) : null}

      {submitError ? (
        <div style={bandStyle("amber")} role="alert">
          {submitError}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <>
          {reduction.skipped > 0 ? (
            <div style={bandStyle("amber")}>
              {fillTemplate(IMPORT_COPY.skippedRowsNote, { n: reduction.skipped })}
            </div>
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: "0.82rem", color: "var(--color-text-2)" }}>
              {estimateState === "pending"
                ? IMPORT_COPY.estimatePending
                : estimateState === "unavailable"
                  ? IMPORT_COPY.estimateUnavailable
                  : estimate
                    ? `${fillTemplate(IMPORT_COPY.estimateLine, {
                        minutes: Math.max(1, Math.round(estimate.estimatedMinutes)),
                        usd: estimate.estimatedUsd.toFixed(2),
                      })}${estimate.calibrated ? "" : ` ${IMPORT_COPY.estimateUncalibrated}`}`
                    : ""}
            </div>
            <button
              type="button"
              disabled={ready === 0 || submitting}
              aria-busy={submitting || undefined}
              onClick={() => void submit()}
              className="rounded-lg px-4 py-2 text-sm disabled:opacity-50"
              style={{
                background: "var(--color-primary)",
                color: "var(--color-surface)",
                fontWeight: 600,
                border: "none",
                cursor: ready === 0 ? "not-allowed" : "pointer",
              }}
            >
              {fillTemplate(IMPORT_COPY.processBatchCount, { n: ready })}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function bandStyle(tone: "neutral" | "amber"): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: "0.85rem",
    ...(tone === "amber"
      ? {
          background: "var(--color-warning-bg)",
          border: "1px solid var(--color-warning-border)",
          color: "var(--color-warning-text)",
        }
      : {
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          color: "var(--color-text-2)",
        }),
  };
}

function smallBtn(primary: boolean): React.CSSProperties {
  return {
    padding: "4px 12px",
    borderRadius: 6,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    border: primary ? "none" : "1px solid var(--color-border)",
    background: primary ? "var(--color-primary)" : "var(--color-surface)",
    color: primary ? "var(--color-surface)" : "var(--color-text-2)",
  };
}
